import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import IvoryMailModal from '../components/modals/IvoryMailModal';
import { ChevronRight, Plus, X, Info } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import { AdmissionActions } from '../components/admission/AdmissionActions';

const PatientAdmissionDetail = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('admission');
  const [showMailModal, setShowMailModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    admissionStatus: 'Admitted',
    admitDate: '',
    pan: '',
    office: 'Ivory Home Health Care, Inc.',
    countyCBSA: '',
    physician: '',
    caseManager: '',
    sourceOfAdmission: 'Clinic Referral',
    referralSource: '',
    transferredFromAgency: false,
    dnr: 'No',
    dischargeDate: '',
    dischargeCode: ''
  });

  useEffect(() => {
    fetchPatient();
  }, [patientId]);

  const fetchPatient = async () => {
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data.success) {
        const p = response.data.patient;
        setPatient(p);
        setFormData({
          admissionStatus: p.status === 'Active' ? 'Admitted' : p.status === 'Discharged' ? 'Discharged' : 'Not Admitted',
          admitDate: p.admitDate || '',
          pan: p.pan || '',
          office: p.officeName || 'Ivory Home Health Care, Inc.',
          countyCBSA: p.countyCBSA || '',
          physician: p.physician || '',
          caseManager: p.caseManager || '',
          sourceOfAdmission: p.sourceOfAdmission || 'Clinic Referral',
          referralSource: p.referralSource || '',
          transferredFromAgency: p.transferredFromAgency || false,
          dnr: p.dnr || 'No',
          dischargeDate: p.dischargeDate || '',
          dischargeCode: p.dischargeCode || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load patient');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const status = { Admitted: 'Active', Discharged: 'Discharged', 'Not Admitted': 'Not Admitted' }[formData.admissionStatus];
    const { admissionStatus, office, ...rest } = formData;
    try {
      await api.put(`/patients/${patientId}`, { ...rest, officeName: office, status });
      setPatient({ ...patient, ...rest, officeName: office, status });
      toast.success('Admission saved successfully');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const tabs = [
    { id: 'admission', label: 'Admission' },
    { id: 'diagnoses', label: 'Diagnoses & Procedures' },
    { id: 'disciplines', label: 'Disciplines' },
    { id: 'frequency', label: 'Frequency' },
    { id: 'other', label: 'Other' }
  ];

  const quickLinks = [
    { label: 'COVID-19 Patient Screening', icon: '📋' },
    { label: '485', icon: '📄' },
    { label: 'Aide Docs', icon: '📄' },
    { label: 'Braden Scale', icon: '📄' },
    { label: 'Ivory Mail', icon: '✉️', onClick: () => setShowMailModal(true) },
    { label: 'Care Team', icon: '👥' },
    { label: 'Claims', icon: '📄' },
    { label: 'Communication Log', icon: '💬' },
    { label: 'Electronic Health Records', icon: '📄' },
    { label: 'Invoice', icon: '📄' },
    { label: 'Med Profile', icon: '💊' },
    { label: 'Missed Visit', icon: '📅' },
    { label: 'MSW', icon: '📄' },
    { label: 'Assessments (OASIS Mgmt)', icon: '📋' },
    { label: 'Patient Demographics', icon: '👤' }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!patient) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Patient not found</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex">
        {/* Main Content */}
        <div className="flex-1 pr-4">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-[#5bc0de] text-[#5bc0de]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
            <div className="ml-auto">
              <button className="bg-[#f0ad4e] text-white px-3 py-1 rounded text-sm">
                GoTo
              </button>
            </div>
          </div>

          {/* Patient Header */}
          <h1 className="text-2xl font-semibold text-[#5bc0de] mb-2">
            {patient.lastName}, {patient.firstName}
          </h1>
          <div className="bg-gray-100 rounded px-4 py-2 inline-block mb-4">
            <span className="text-gray-600">DOB</span>
            <span className="ml-2 font-medium">{formatDate(patient.dateOfBirth)}</span>
          </div>

          {/* COVID-19 Screening Banner */}
          <div className="bg-[#fcf8e3] border border-[#faebcc] rounded-lg p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[#f0ad4e] text-xl">⚠</span>
              <span className="text-[#8a6d3b]">Most Recent Screening : None - Risk Level : N/A</span>
            </div>
            <button className="bg-[#5bc0de] text-white px-3 py-1 rounded text-sm">
              Go to Screening
            </button>
          </div>

          {/* Admission Section */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
            <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium">
              Admission
            </div>
            <div className="p-4 space-y-4">
              {/* Admission Status */}
              <div className="flex items-center gap-8">
                <label className="text-gray-700 font-medium w-40">Admission Status</label>
                <div className="flex gap-6">
                  {['Admitted', 'Discharged', 'Not Admitted'].map(status => (
                    <label key={status} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="admissionStatus"
                        value={status}
                        checked={formData.admissionStatus === status}
                        onChange={(e) => setFormData({...formData, admissionStatus: e.target.value})}
                        className="w-4 h-4 text-[#5bc0de]"
                      />
                      <span className="text-gray-700">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Row: Admit Date & PAN */}
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-40">Admit Date</label>
                  <input
                    type="date"
                    value={formData.admitDate}
                    onChange={(e) => setFormData({...formData, admitDate: e.target.value})}
                    className="border border-gray-300 rounded px-3 py-1.5 flex-1"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-48">Patient Account Number (PAN)</label>
                  <input
                    type="text"
                    value={formData.pan}
                    onChange={(e) => setFormData({...formData, pan: e.target.value})}
                    className="border border-gray-300 rounded px-3 py-1.5 w-24"
                  />
                </div>
              </div>

              {/* Row: Office & County */}
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-40">Office</label>
                  <select
                    value={formData.office}
                    onChange={(e) => setFormData({...formData, office: e.target.value})}
                    className="border border-gray-300 rounded px-3 py-1.5 flex-1"
                  >
                    <option>Ivory Home Health Care, Inc.</option>
                  </select>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-48">County/CBSA</label>
                  <input
                    type="text"
                    value={formData.countyCBSA}
                    onChange={(e) => setFormData({...formData, countyCBSA: e.target.value})}
                    className="border border-gray-300 rounded px-3 py-1.5 flex-1"
                  />
                </div>
              </div>

              {/* Row: Physician & Case Manager */}
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-40">Physician</label>
                  <div className="flex-1">
                    <select
                      value={formData.physician}
                      onChange={(e) => setFormData({...formData, physician: e.target.value})}
                      className="border border-gray-300 rounded px-3 py-1.5 w-full"
                    >
                      <option value="">Select Physician</option>
                      <option value="ROOZROKH, BABAK">ROOZROKH, BABAK</option>
                    </select>
                    <div className="flex gap-4 mt-1">
                      <button className="text-[#5bc0de] text-sm flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add Physician
                      </button>
                      <button className="text-[#5bc0de] text-sm flex items-center gap-1">
                        <Info className="w-3 h-3" /> Physician Info
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-48">Case Manager/Primary RN</label>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.caseManager}
                      onChange={(e) => setFormData({...formData, caseManager: e.target.value})}
                      className="border border-gray-300 rounded px-3 py-1.5 w-full"
                    />
                    <button className="text-[#5bc0de] text-sm mt-1">Add Caregiver</button>
                  </div>
                </div>
              </div>

              {/* Row: Source & Referral */}
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-40">Source of Admission</label>
                  <select
                    value={formData.sourceOfAdmission}
                    onChange={(e) => setFormData({...formData, sourceOfAdmission: e.target.value})}
                    className="border border-gray-300 rounded px-3 py-1.5 flex-1"
                  >
                    <option>Clinic Referral</option>
                    <option>Hospital</option>
                    <option>Physician Referral</option>
                    <option>Self Referral</option>
                  </select>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-48">Referral Source</label>
                  <input
                    type="text"
                    value={formData.referralSource}
                    onChange={(e) => setFormData({...formData, referralSource: e.target.value})}
                    className="border border-gray-300 rounded px-3 py-1.5 flex-1"
                  />
                </div>
              </div>

              {/* Transferred Checkbox */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.transferredFromAgency}
                    onChange={(e) => setFormData({...formData, transferredFromAgency: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-gray-700">Transferred from another Home Health Agency</span>
                </label>
              </div>

              {/* DNR */}
              <div className="flex items-center gap-8">
                <label className="text-gray-700 font-medium w-40">DNR:</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 border border-gray-300 rounded px-3 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.dnr === 'Yes'}
                      onChange={() => setFormData({...formData, dnr: formData.dnr === 'Yes' ? 'No' : 'Yes'})}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center gap-2 border border-gray-300 rounded px-3 py-1 cursor-pointer bg-gray-100">
                    <input
                      type="checkbox"
                      checked={formData.dnr === 'No'}
                      onChange={() => setFormData({...formData, dnr: formData.dnr === 'No' ? 'Yes' : 'No'})}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {/* Discharge */}
              <div className="grid grid-cols-2 gap-8">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-40">Discharge Date</label>
                  <div className="flex-1">
                    <input
                      type="date"
                      value={formData.dischargeDate}
                      onChange={(e) => setFormData({...formData, dischargeDate: e.target.value})}
                      className="border border-gray-300 rounded px-3 py-1.5 w-full"
                    />
                    <button className="text-[#5bc0de] text-sm mt-1">Discharge Summary</button>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium w-48">Discharge Code</label>
                  <input
                    type="text"
                    value={formData.dischargeCode}
                    onChange={(e) => setFormData({...formData, dischargeCode: e.target.value})}
                    placeholder="Enter Discharge Code"
                    className="border border-gray-300 rounded px-3 py-1.5 flex-1"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={handleSave} className="bg-[#5bc0de] text-white px-4 py-1.5 rounded text-sm hover:bg-[#46b8da]" data-testid="save-admission-btn">
                  Save Admission
                </button>
              </div>
            </div>
          </div>

          {/* Insurance & Prior Auth Section */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
            <div className="px-4 py-3 border-b flex items-center gap-4">
              <h2 className="text-lg font-medium text-[#5bc0de]">Insurance & Prior Auth</h2>
              <button className="bg-[#5bc0de] text-white px-3 py-1 rounded text-sm flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add Insurance
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                  <th className="px-4 py-2 text-left text-gray-600">Responsibility</th>
                  <th className="px-4 py-2 text-left text-gray-600">Payer</th>
                  <th className="px-4 py-2 text-left text-gray-600">Effective From</th>
                  <th className="px-4 py-2 text-left text-gray-600">Effective Thru</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="px-4 py-2">
                    <button className="bg-[#5bc0de] text-white px-2 py-1 rounded text-xs">
                      ☰
                    </button>
                  </td>
                  <td className="px-4 py-2">Primary</td>
                  <td className="px-4 py-2">{patient.insurance || 'CIGNA'}</td>
                  <td className="px-4 py-2">{formatDate(patient.insuranceEffectiveDate) || '08/22/2025'}</td>
                  <td className="px-4 py-2">{formatDate(patient.insuranceTerminationDate) || '10/19/2025'}</td>
                </tr>
              </tbody>
            </table>
            <div className="p-4 flex justify-between">
              <button className="bg-[#d9534f] text-white px-4 py-1.5 rounded text-sm">
                Delete
              </button>
              <button className="bg-[#5bc0de] text-white px-4 py-1.5 rounded text-sm">
                Next Tab
              </button>
            </div>
          </div>

          {/* Bottom Action Buttons */}
          <AdmissionActions patient={patient} admission={formData} onPatientChange={setPatient} />
        </div>

        {/* Right Sidebar - Quick Links */}
        <div className="w-64 border-l border-gray-200 pl-4">
          <div className="space-y-1">
            {quickLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={link.onClick}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded text-left"
              >
                <span className="text-[#5bc0de]">📄</span>
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Ivory Mail Modal */}
      {showMailModal && (
        <IvoryMailModal
          patient={patient}
          onClose={() => setShowMailModal(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default PatientAdmissionDetail;
